'use client';

import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTranslation } from '@/hooks/useTranslation';
import { appendInviteCodeToPath } from '@/lib/inviteCode';

interface InviteCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  message?: string;
  initialValue?: string;
  onConfirm?: (inviteCode: string) => void;
}

interface InviteCodeDialogContentProps {
  title?: string;
  message?: string;
  initialValue?: string;
  onOpenChange: (open: boolean) => void;
  onConfirm?: (inviteCode: string) => void;
}

function InviteCodeDialogContentBody({
  title,
  message,
  initialValue = '',
  onOpenChange,
  onConfirm,
}: InviteCodeDialogContentProps) {
  const { t } = useTranslation();
  const [inviteCode, setInviteCode] = useState(initialValue);
  const [inlineMessage, setInlineMessage] = useState(message);
  const inputRef = useRef<HTMLInputElement>(null);
  const visibleMessage = inlineMessage;
  const errorMessageId = visibleMessage ? 'invite-code-error-message' : undefined;

  const handleSubmit = async () => {
    if (!inviteCode.trim()) return;
    const normalizedCode = inviteCode.trim();

    if (onConfirm) {
      onConfirm(normalizedCode);
      onOpenChange(false);
      return;
    }

    // 将邀请码通过 URL 传递给登录页 rather than 本地存储
    const nextUrl = appendInviteCodeToPath('/login', normalizedCode);
    window.location.href = nextUrl;
    setInviteCode('');
    onOpenChange(false);
  };

  return (
    <DialogContent
      className="min-w-0 sm:max-w-sm"
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        const input = inputRef.current;
        if (input) {
          input.focus({ preventScroll: true });
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>{title ?? t('login.inviteDialog.title')}</DialogTitle>
        <DialogDescription>
          {/*{t('login.inviteDialog.description')}*/}
        </DialogDescription>
      </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
           <Input
             ref={inputRef}
             id="invite-code"
             type="text"
             variant={visibleMessage ? 'error' : 'default'}
             placeholder={t('login.inviteDialog.codePlaceholder')}
             value={inviteCode}
             onChange={(e) => {
                if (visibleMessage) {
                 setInlineMessage(undefined);
                }
                setInviteCode(e.target.value);
              }}
             aria-invalid={!!visibleMessage}
             aria-describedby={errorMessageId}
             onKeyDown={(e) => {
               if (e.key === 'Enter') {
                 e.preventDefault();
                 handleSubmit();
               }
             }}
             className="h-10"
           />
           {visibleMessage && (
             <p id={errorMessageId} className="text-sm text-red-600 dark:text-red-400">
               {visibleMessage}
             </p>
           )}
         </div>
         {/*<div className="rounded-lg bg-zinc-50 dark:bg-zinc-800 p-3 text-sm text-zinc-600 dark:text-zinc-400">
           <p className="font-medium mb-1">{t('login.inviteDialog.codeBenefits')}</p>
           <ul className="list-disc list-inside space-y-1">
             <li>{t('login.inviteDialog.benefit1')}</li>
             <li>{t('login.inviteDialog.benefit2')}</li>
             <li>{t('login.inviteDialog.benefit3')}</li>
           </ul>
         </div>*/}
       </div>

      <DialogFooter>
        <Button
          variant="outline"
          className="w-full sm:w-auto"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onOpenChange(false);
            }
          }}
          onClick={() => onOpenChange(false)}
        >
          {t('login.inviteDialog.cancel')}
        </Button>
        <Button
          variant="primary"
          className="w-full sm:w-auto"
          data-track-event="cta_clicked"
          data-track-element="confirm_invite_code"
          data-track-section="login"
          data-track-page="login"
          data-track-module="invite"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
          onClick={handleSubmit}
          disabled={!inviteCode.trim()}
        >
          {t('login.inviteDialog.confirm')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export function InviteCodeDialog({
  open,
  onOpenChange,
  title,
  message,
  initialValue = '',
  onConfirm,
}: InviteCodeDialogProps) {
  const dialogSessionKey = open
    ? `${title ?? ''}:${message ?? ''}:${initialValue}`
    : 'closed';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <InviteCodeDialogContentBody
        key={dialogSessionKey}
        title={title}
        message={message}
        initialValue={initialValue}
        onOpenChange={onOpenChange}
        onConfirm={onConfirm}
      />
    </Dialog>
  );
}
